import React, { useEffect, useState } from "react";
import { Info, Copy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";

/**
 * BuildInfoCard — surfaces the app's build fingerprint so users
 * (and support staff) can identify exactly which JS bundle + native
 * binary is running without needing Capgo dashboard access.
 *
 * On web: shows platform + "web build" (no OTA involved).
 * On TestFlight/App Store iOS: shows OTA bundle version + native version.
 *
 * Iter-84 addition — reduces support back-and-forth on OTA-related issues
 * ("Which bundle did the user see the bug on?").
 */
const BuildInfoCard = () => {
  const [info, setInfo] = useState({
    platform: "web",
    bundle: "native",
    native: "—",
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = { platform: "web", bundle: "native", native: "—", loading: false };
      try {
        const cap = await import("@capacitor/core").catch(() => null);
        if (cap?.Capacitor) {
          next.platform = cap.Capacitor.getPlatform?.() || "web";
        }
      } catch (_) {
        // web preview — no capacitor
      }
      try {
        const mod = await import("@capgo/capacitor-updater").catch(() => null);
        if (mod?.CapacitorUpdater) {
          const current = await mod.CapacitorUpdater.current?.().catch(() => null);
          if (current?.bundle?.version) next.bundle = current.bundle.version;
          // The web stub returns native='0.0.0' — treat as "not native".
          if (current?.native && current.native !== "0.0.0") next.native = current.native;
        }
      } catch (_) {
        // web preview — no capgo
      }
      if (!cancelled) setInfo(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copyAll = async () => {
    const line = `platform=${info.platform} bundle=${info.bundle} native=${info.native}`;
    try {
      await navigator.clipboard.writeText(line);
      toast.success("Build info copied.");
    } catch (_) {
      toast.error("Copy failed.");
    }
  };

  return (
    <Card data-testid="build-info-card" className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Info className="h-4 w-4 text-sky-400" />
          Build info
        </CardTitle>
        <CardDescription className="text-xs">
          Share this with support if you hit an issue — it tells us which bundle you&apos;re on.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:text-sm font-mono">
          <dt className="text-gray-500">Platform</dt>
          <dd className="text-gray-200" data-testid="build-info-platform">{info.platform}</dd>
          <dt className="text-gray-500">OTA bundle</dt>
          <dd className="text-gray-200" data-testid="build-info-bundle">{info.loading ? "…" : info.bundle}</dd>
          <dt className="text-gray-500">Native version</dt>
          <dd className="text-gray-200" data-testid="build-info-native">{info.native}</dd>
        </dl>
        <Button
          size="sm"
          variant="ghost"
          onClick={copyAll}
          disabled={info.loading}
          data-testid="build-info-copy-btn"
          className="mt-3 text-xs text-gray-400 hover:text-gray-200 h-7 px-2"
        >
          <Copy className="h-3 w-3 mr-1.5" />
          Copy
        </Button>
      </CardContent>
    </Card>
  );
};

export default BuildInfoCard;
